import moment from "moment-timezone";
import _get from "lodash/get";

import { PRIMARY_DATE_FORMAT } from "../constants/formats";

/**
 * Formats a date for display in the given timezone.
 *
 * Date-only values are stored as an end-of-day UTC instant in the project's
 * timezone (see getEndOfDayInTimezone / DatePickerWithError). They must be read
 * back in that same timezone, otherwise the calendar day shifts in the viewer's
 * local time.
 *
 * @param {string|Date} value - The UTC date/instant to format.
 * @param {string} timezone - IANA timezone (e.g. "America/Denver").
 * @param {string} [format] - moment format string. Defaults to PRIMARY_DATE_FORMAT.
 * @returns {string|null} The formatted date, or null when value is empty.
 */
export const formatDateInTimezone = (
  value,
  timezone,
  format = PRIMARY_DATE_FORMAT
) => {
  if (!value) return null;

  return timezone
    ? moment.tz(value, timezone).format(format)
    : moment(value).format(format);
};

export const getTimezoneFromScope = scopeName => {
  if (!scopeName) {
    return "America/Denver";
  }

  const normalizedName = scopeName.toUpperCase();

  if (normalizedName.includes("UK")) {
    return "Europe/London";
  }

  return "America/Denver";
};

export const getEndOfDayInTimezone = (date, timezone) => {
  if (!date) return null;
  return moment
    .tz(date, timezone)
    .endOf("day")
    .toDate();
};

export const getTimezoneFromProject = project => {
  const scopeName = _get(project, "office.scope.name", null);
  return getTimezoneFromScope(scopeName);
};

export const getTimezoneFromClient = client => {
  const scopeName = _get(client, "scope.name", null);
  return getTimezoneFromScope(scopeName);
};

export const getTimezoneFromPurchaseOrder = purchaseOrder => {
  const scopeName = _get(purchaseOrder, "project.office.scope.name", null);
  return getTimezoneFromScope(scopeName);
};

export const getTimezoneFromInvoice = invoice => {
  const scopeName = _get(
    invoice,
    "purchaseOrder.project.office.scope.name",
    null
  );
  return getTimezoneFromScope(scopeName);
};

export const getTimezoneFromBidGroup = bidGroup => {
  const scopeName = _get(bidGroup, "project.office.scope.name", null);
  return getTimezoneFromScope(scopeName);
};

export const getTimezoneFromSpec = spec => {
  const scopeName = _get(spec, "project.office.scope.name", null);
  return getTimezoneFromScope(scopeName);
};

export const getTimezoneFromUser = user => {
  const scopeName = _get(user, "office.scope.name", null);
  return getTimezoneFromScope(scopeName);
};
